import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type CardElevation = 'flat' | 'raised';

/**
 * A surface that groups related content.
 *
 * Three slots: a default one for the body, plus named header and footer slots
 * that collapse to nothing when empty, so a card with only a body has no stray
 * padding where a header would have been.
 *
 * @example <v-card elevation="raised"><h2 slot="header">Title</h2>Body</v-card>
 */
@customElement('v-card')
export class VCard extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: var(--v-font-family-body);
      font-size: var(--v-type-body-medium-font-size);
      line-height: var(--v-type-body-medium-line-height);
      color: var(--v-colors-text-primary);
      background-color: var(--v-colors-surface-primary);
      border-style: solid;
      border-width: var(--v-control-border-width);
      border-color: var(--v-colors-borders);
      border-radius: var(--v-surface-radius);
      padding: var(--v-surface-padding);
    }

    :host([elevation='raised']) {
      background-color: var(--v-colors-surface-secondary);
    }

    /* A slot with nothing in it should take no space at all. CSS cannot see
       whether a slot has assigned content, so slotchange decides it. */
    [hidden] {
      display: none;
    }

    .header {
      margin-block-end: var(--v-stack-gap);
    }

    .footer {
      margin-block-start: var(--v-stack-gap);
    }
  `;


  @property({ reflect: true }) elevation: CardElevation = 'flat';

  @state() private hasHeader = false;

  @state() private hasFooter = false;

  #onSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;
    const filled = slot
      .assignedNodes({ flatten: true })
      .some((node) => node.nodeType === Node.ELEMENT_NODE || (node.textContent ?? '').trim() !== '');

    if (slot.name === 'header') this.hasHeader = filled;
    if (slot.name === 'footer') this.hasFooter = filled;
  }

  override render() {
    return html`
      <div class="header" ?hidden=${!this.hasHeader}>
        <slot name="header" @slotchange=${this.#onSlotChange}></slot>
      </div>
      <div class="body"><slot></slot></div>
      <div class="footer" ?hidden=${!this.hasFooter}>
        <slot name="footer" @slotchange=${this.#onSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'v-card': VCard;
  }
}
